import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import MovieCard from '@/components/feature/MovieCard';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';
import { movieCategories } from '@/mocks/movies';
import { allMovies, sortMovies, sortOptions, type SortOption } from '@/utils/movie';

const PAGE_SIZE = 8;

export default function MovieCategory() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTypes = useMemo(() => {
    const raw = searchParams.get('type');
    return raw && movieCategories.includes(raw) ? [raw] : [];
  }, [searchParams]);

  const [selectedTypes, setSelectedTypes] = useState<string[]>(initialTypes);
  const [sort, setSort] = useState<SortOption['value']>('updated');
  const [visible, setVisible] = useState(PAGE_SIZE);

  useEffect(() => {
    setSelectedTypes(initialTypes);
  }, [initialTypes]);

  useEffect(() => {
    setVisible(PAGE_SIZE);
  }, [selectedTypes, sort]);

  const filtered = useMemo(() => {
    const base =
      selectedTypes.length === 0
        ? allMovies
        : allMovies.filter((movie) => movie.types.some((type) => selectedTypes.includes(type)));
    return sortMovies(base, sort);
  }, [selectedTypes, sort]);

  const list = filtered.slice(0, visible);

  const toggleType = (type: string) => {
    setSelectedTypes((prev) => (prev.includes(type) ? prev.filter((item) => item !== type) : [...prev, type]));
  };

  const resetFilters = () => {
    setSelectedTypes([]);
    setSort('updated');
    setSearchParams({});
  };

  const breadcrumbLabel = selectedTypes.length === 0 ? '全部影片' : selectedTypes.join(' / ');
  const pageTitle = `${breadcrumbLabel}电影免费在线观看 - 善良的小姨子影视站`;

  useDocumentMeta({
    title: pageTitle,
    description: `善良的小姨子影视站${breadcrumbLabel}电影列表，涵盖剧情、家庭、悬疑、爱情等类型，提供每部影片的剧情简介与在线观看入口。`,
    keywords: `${breadcrumbLabel},电影分类,免费在线观看,善良的小姨子影视站,高清电影`,
  });

  return (
    <SiteLayout>
      <section className="w-full px-4 md:px-8 pt-8 md:pt-12 pb-6 border-b border-background-800 bg-background-950">
        <nav className="flex items-center gap-2 text-xs md:text-sm text-foreground-400 mb-5 flex-wrap">
          <Link to="/" className="hover:text-primary-400 transition-colors cursor-pointer">
            首页
          </Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-foreground-200">电影分类</span>
          {selectedTypes.length > 0 && (
            <>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-primary-400">{selectedTypes.join(' / ')}</span>
            </>
          )}
        </nav>

        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <span className="text-xs font-display tracking-[0.3em] text-accent-400">MOVIE CATEGORY</span>
            <h1 className="mt-2 text-2xl md:text-4xl font-heading font-black text-foreground-50">
              {breadcrumbLabel}电影
            </h1>
            <p className="mt-3 text-sm text-foreground-400 max-w-2xl">
              按类型筛选你想看的影片，支持多选联合筛选，并可按更新时间、热度或评分排序。
            </p>
          </div>
          <div className="text-sm text-foreground-400 whitespace-nowrap">
            共 <span className="text-primary-400 font-semibold">{filtered.length}</span> 部影片
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-8 py-8 bg-background-950">
        <div className="rounded-lg bg-background-900 border border-background-800 p-4 md:p-5">
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
            <div className="flex-1">
              <span className="block text-xs text-foreground-400 mb-3">影片类型（可多选）</span>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setSelectedTypes([])}
                  className={`px-3.5 py-1.5 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap ${
                    selectedTypes.length === 0
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-800 text-foreground-300 hover:bg-background-700'
                  }`}
                >
                  全部
                </button>
                {movieCategories.map((type) => {
                  const active = selectedTypes.includes(type);
                  return (
                    <button
                      key={type}
                      type="button"
                      onClick={() => toggleType(type)}
                      className={`px-3.5 py-1.5 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap ${
                        active
                          ? 'bg-primary-500 text-background-50'
                          : 'bg-background-800 text-foreground-300 hover:bg-background-700'
                      }`}
                    >
                      {type}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="lg:w-48 shrink-0">
              <span className="block text-xs text-foreground-400 mb-3">排序方式</span>
              <div className="relative">
                <select
                  value={sort}
                  onChange={(event) => setSort(event.target.value as SortOption['value'])}
                  className="w-full appearance-none px-4 py-2 pr-10 rounded-md bg-background-800 border border-background-700 text-sm text-foreground-100 outline-none focus:border-primary-500 transition-colors cursor-pointer"
                >
                  {sortOptions.map((option) => (
                    <option key={option.value} value={option.value} className="bg-background-800">
                      {option.label}
                    </option>
                  ))}
                </select>
                <i className="ri-arrow-down-s-line absolute right-3 top-1/2 -translate-y-1/2 text-foreground-400 pointer-events-none"></i>
              </div>
            </div>
          </div>
        </div>

        {list.length > 0 ? (
          <>
            <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
              {list.map((movie) => (
                <MovieCard key={movie.id} movie={movie} />
              ))}
            </div>

            <div className="mt-10 flex flex-col items-center gap-4">
              {visible < filtered.length ? (
                <button
                  type="button"
                  onClick={() => setVisible((prev) => prev + PAGE_SIZE)}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-background-800 border border-background-700 text-sm text-foreground-100 hover:border-primary-500/60 hover:bg-background-700 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-add-line"></i>
                  加载更多（剩余 {filtered.length - visible} 部）
                </button>
              ) : (
                <p className="text-xs text-foreground-500">已经到底啦，共 {filtered.length} 部影片</p>
              )}
            </div>
          </>
        ) : (
          <div className="mt-10 flex flex-col items-center justify-center text-center py-16 rounded-lg bg-background-900 border border-background-800">
            <span className="w-16 h-16 rounded-full bg-background-800 flex items-center justify-center text-foreground-400">
              <i className="ri-inbox-archive-line text-3xl"></i>
            </span>
            <h3 className="mt-5 text-lg font-semibold text-foreground-100">没有找到匹配的影片</h3>
            <p className="mt-2 text-sm text-foreground-400 max-w-md">
              当前筛选条件组合下暂无影片，试试减少筛选条件或重置筛选。
            </p>
            <button
              type="button"
              onClick={resetFilters}
              className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-refresh-line"></i>
              重置筛选
            </button>
          </div>
        )}
      </section>
    </SiteLayout>
  );
}